// src/pages/CompanySLA.js
import React, { useState } from 'react';
import './SLAs.css';
import { FaBullseye, FaUsers, FaClock, FaExclamationTriangle, FaPhone, FaEnvelope, FaChartBar, FaCheckCircle, FaBook, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const tocSections = [
  { id: 'purpose', icon: <FaBullseye />, label: 'Purpose' },
  { id: 'scope', icon: <FaUsers />, label: 'Scope' },
  { id: 'timelines', icon: <FaClock />, label: 'Timelines' },
  { id: 'escalation', icon: <FaExclamationTriangle />, label: 'Escalation' },
  { id: 'channels', icon: <FaPhone />, label: 'Channels' },
  { id: 'compliance', icon: <FaChartBar />, label: 'Compliance' },
  { id: 'violations', icon: <FaExclamationTriangle />, label: 'Violations' },
  { id: 'review', icon: <FaBook />, label: 'Review' },
];

function SLATOC() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sla-toc-collapsible">
      <button className="sla-toc-toggle" onClick={() => setOpen(o => !o)}>
        {open ? <FaChevronUp /> : <FaChevronDown />} Table of Contents
      </button>
      {open && (
        <ul>
          {tocSections.map(s => (
            <li key={s.id}><a href={`#${s.id}`}>{s.icon} {s.label}</a></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompanySLA() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="sla-dashboard">
      <div className="sla-main-content">
        <h1 className="sla-title">NESIM INTERNAL SERVICE LEVEL AGREEMENT (SLA) ON CUSTOMER COMMUNICATION</h1>
        <SLATOC />
        <section className="sla-section" id="purpose">
          <h2><FaBullseye className="sla-icon" /> 1. Purpose</h2>
          <p>
            This Service Level Agreement (SLA) establishes the expected communication timelines between NESIM and its customers.
            The goal is to ensure a consistent, professional, and timely response to all customer interactions while improving
            service quality and customer satisfaction.
          </p>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="scope">
          <h2><FaUsers className="sla-icon" /> 2. Scope</h2>
          <p>
            This SLA applies to all NESIM employees responsible for handling customer communications, including inquiries,
            complaints, technical support, and billing-related issues. The agreement covers the following communication channels:
          </p>
          <ul>
            <li><FaEnvelope className="sla-list-icon" /> Email</li>
            <li><FaPhone className="sla-list-icon" /> Phone Support</li>
            <li><FaChartBar className="sla-list-icon" /> Live Chat (WhatsApp)</li>
            <li><FaUsers className="sla-list-icon" /> In-Person Appointments</li>
          </ul>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="timelines">
          <h2><FaClock className="sla-icon" /> 3. Communication Timelines</h2>
          <div className="sla-table-scroll">
            <table className="sla-table">
              <thead>
                <tr>
                  <th>Communication Type</th>
                  <th>First Response Time</th>
                  <th>Follow-up Response Time</th>
                  <th>Resolution Time (If applicable)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>General Inquiry</td>
                  <td>Within 4 hours</td>
                  <td>Every 24 hours until resolved</td>
                  <td>48 hours Max</td>
                </tr>
                <tr>
                  <td>Sales Inquiry (Quotation/Service Inquiry)</td>
                  <td>Within 2 hours</td>
                  <td>Every 12 hours until deal closure</td>
                  <td>Dependent on customer decision</td>
                </tr>
                <tr>
                  <td>Billing & Payment Queries</td>
                  <td>Within 4 hours</td>
                  <td>Every 24 hours until resolved</td>
                  <td>3 business days</td>
                </tr>
                <tr>
                  <td>Servicing Equipment</td>
                  <td>Within 1 hour</td>
                  <td>Every 6 hours until resolved</td>
                  <td>3 business days</td>
                </tr>
                <tr>
                  <td>Urgent Escalations (System Failures, Major Service Disruption)</td>
                  <td>Within 30 minutes</td>
                  <td>Every 2 hours until resolved</td>
                  <td>ASAP (Max 12 hours)</td>
                </tr>
                <tr>
                  <td>Customer Complaints</td>
                  <td>Within 4 hours</td>
                  <td>Every 24 hours until resolved</td>
                  <td>Within 5 business days</td>
                </tr>
                <tr>
                  <td>Refund/Service Cancellation Request</td>
                  <td>Within 4 hours</td>
                  <td>Every 48 hours until processed</td>
                  <td>7 business days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="escalation">
          <h2><FaExclamationTriangle className="sla-icon" /> 4. Escalation Procedures</h2>
          <p>
            If an issue is not resolved within the expected resolution time, it will be escalated as follows:
          </p>
          <div className="sla-table-scroll">
            <table className="sla-table">
              <thead>
                <tr>
                  <th>Escalation Level</th>
                  <th>Criteria for Escalation</th>
                  <th>Responsible Team Member</th>
                  <th>Action Taken</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Level 1</td>
                  <td>Initial response delay or issue unresolved after expected time</td>
                  <td>Technical Sales or Office Admin</td>
                  <td>Provide an update & re-prioritize</td>
                </tr>
                <tr>
                  <td>Level 2</td>
                  <td>Issue unresolved past resolution deadline</td>
                  <td>Team Lead/Manager</td>
                  <td>Direct intervention & expedited resolution</td>
                </tr>
                <tr>
                  <td>Level 3</td>
                  <td>Major disruption, high-value customer complaint, or legal risk</td>
                  <td>Managing Director</td>
                  <td>Immediate resolution & formal response</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="channels">
          <h2><FaPhone className="sla-icon" /> 5. Communication Channels & Availability</h2>
          <div className="sla-table-scroll">
            <table className="sla-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Availability</th>
                  <th>Response Time</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Email (support@nesim.co.ke)</td>
                  <td>9 AM - 6 PM, Monday to Friday</td>
                  <td>Within 4 hours</td>
                </tr>
                <tr>
                  <td>Phone Support</td>
                  <td>8 AM - 8 PM, Monday to Friday</td>
                  <td>Within 1 hour</td>
                </tr>
                <tr>
                  <td>Live Chat (WhatsApp)</td>
                  <td>9 AM - 6 PM, Monday to Friday</td>
                  <td>Within 30 minutes</td>
                </tr>
                <tr>
                  <td>In-Person Meetings</td>
                  <td>By appointment only</td>
                  <td>Within 2 business days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="compliance">
          <h2><FaChartBar className="sla-icon" /> 6. SLA Compliance & Monitoring</h2>
          <div className="sla-callout">
            <h3 className="sla-callout-title">SLA Compliance Tip</h3>
            <p className="sla-callout-text">Track SLA metrics monthly, and review exceptions quarterly to stay aligned with customer expectations.</p>
          </div>
          <ul>
            <li>Track response times using Xero CRM or a ticketing system.</li>
            <li>Send monthly reports on SLA performance.</li>
            <li>Review &amp; adjust SLA every quarter based on feedback and performance.</li>
            <li>Train staff on communication best practices and escalation protocols.</li>
          </ul>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="violations">
          <h2><FaExclamationTriangle className="sla-icon" /> 7. SLA Violations & Consequences</h2>
          <ul>
            <li>Acknowledge the delay and provide a formal apology to the customer.</li>
            <li>Offer compensation or goodwill gestures if necessary.</li>
            <li>Investigate root causes and take corrective action to prevent future issues.</li>
            <li>Implement internal measures such as process improvements or additional training.</li>
          </ul>
        </section>
        <div className="sla-divider" />
        <section className="sla-section" id="review">
          <h2><FaBook className="sla-icon" /> 8. Review & Amendments</h2>
          <p>
            This SLA is subject to periodic review and updates based on company needs and customer feedback. Any changes must be
            approved by the management team and communicated to relevant staff members.
          </p>
        </section>
      </div>
    </div>
  );
}

export default CompanySLA;
